import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import { Textarea } from "@/shared/components/ui/textarea";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/shared/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import type { Schema } from "hast-util-sanitize";
import { useEffect, useState } from "react";
import type { UiReportStepRow } from "../../../queries/reports";
import { StatusPill } from "@/shared/components/status-pill";
import { ChevronDown, ChevronUp, Play, Trash2 } from "lucide-react";
import { formatStepName } from "../lib/format-step-name";
import { cn } from "@/shared/lib/utils";
import { DisplayInputResultLabels } from "./display-input-result-labels";
import { TemplateOriginLink } from "./template-origin-link";
import { useReportStepOpenState } from "../hooks/reportstep/use-report-step-open-state";

const markdownSanitizeSchema: Schema = {
  ...defaultSchema,
  tagNames: [...new Set([...(defaultSchema.tagNames ?? []), "br"])],
  attributes: {
    ...(defaultSchema.attributes ?? {}),
    br: [],
  },
};

interface StepCardProps {
  step: UiReportStepRow;
  isEditing: boolean;
  isSaving: boolean;
  draft: string;
  saveError: string | null;
  isStarting?: boolean;
  isDeleting?: boolean;
  isReadOnly?: boolean;
  executionUnavailable?: boolean;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  onDraftChange: (value: string) => void;
  onStart?: () => void;
  onDelete?: () => void;
}

export function StepCard({
  step,
  isEditing,
  isSaving,
  draft,
  saveError,
  isStarting = false,
  isDeleting = false,
  isReadOnly = false,
  executionUnavailable = false,
  onEdit,
  onSave,
  onCancel,
  onDraftChange,
  onStart,
  onDelete,
}: StepCardProps) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [open, setOpen] = useReportStepOpenState(step.analysis_step_id);
  const stepTitle =
    typeof step.display_name === "string" && step.display_name.trim()
      ? step.display_name
      : formatStepName(step.analysis_step_name);
  const isStepEditing = isEditing && !isReadOnly;

  useEffect(() => {
    if (isStepEditing) setOpen(true);
  }, [isStepEditing, setOpen]);

  const handleDeleteClick = () => {
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    setShowDeleteDialog(false);
    onDelete?.();
  };

  const handleCancelDelete = () => {
    setShowDeleteDialog(false);
  };
  return (
    <Card
      id={`report-step-${step.analysis_step_id}`}
      className={cn(
        "scroll-mt-20 gap-0 border-muted/60 py-0",
        isReadOnly ? "border-muted bg-muted/30 opacity-80" : undefined,
      )}
    >
      <CardHeader className="space-y-2 px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <CardTitle className="text-base">{stepTitle}</CardTitle>
              {step.templateOrigin && (
                <TemplateOriginLink origin={step.templateOrigin} iconOnly />
              )}
              {step.analysis_step_status && (
                <StatusPill status={step.analysis_step_status} />
              )}
              {isReadOnly && (
                <Badge variant="outline" className="text-xs">
                  Read-only
                </Badge>
              )}
            </div>
            <DisplayInputResultLabels
              labels={step.displayInputResults}
              className="pt-1"
            />
          </div>
          <div className="flex items-center gap-2">
            {!isStepEditing && !isReadOnly && (
              <>
                {onStart && (
                  <Button
                    size="sm"
                    variant="outlineprimary"
                    onClick={onStart}
                    disabled={
                      executionUnavailable ||
                      isStarting ||
                      step.analysis_step_status?.toLowerCase() !== "completed"
                    }
                  >
                    <Play
                      className={`h-4 w-4 mr-1 ${isStarting ? "animate-pulse" : ""}`}
                    />
                    {isStarting ? "Starting..." : "Start"}
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={onEdit}>
                  Edit
                </Button>
                {onDelete && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleDeleteClick}
                    disabled={isDeleting}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </>
            )}
            {isStepEditing && (
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={onSave} disabled={isSaving}>
                  {isSaving ? "Saving..." : "Save"}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onCancel}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
              </div>
            )}
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={() => setOpen((value) => !value)}
              aria-label={open ? "Collapse step" : "Expand step"}
            >
              {open ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </CardHeader>
      {open && (
        <CardContent className="space-y-3 border-t px-4 py-4">
          {isStepEditing ? (
            <Tabs defaultValue="write" className="w-full">
              <TabsList>
                <TabsTrigger value="write">Write</TabsTrigger>
                <TabsTrigger value="preview">Preview</TabsTrigger>
              </TabsList>
              <TabsContent value="write" className="mt-3">
                <Textarea
                  value={draft}
                  onChange={(e) => onDraftChange(e.target.value)}
                  className="min-h-40"
                  placeholder="Write step result in Markdown..."
                />
              </TabsContent>
              <TabsContent value="preview" className="mt-3">
                <div className="prose prose-sm max-w-none dark:prose-invert min-h-40 rounded-md border border-input bg-transparent px-3 py-2">
                  {draft ? (
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[
                        rehypeRaw,
                        [rehypeSanitize, markdownSanitizeSchema],
                      ]}
                    >
                      {draft}
                    </ReactMarkdown>
                  ) : (
                    <p className="text-muted-foreground">
                      Nothing to preview yet.
                    </p>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          ) : (
            <div className="prose prose-sm max-w-none dark:prose-invert">
              {step.value ? (
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[
                    rehypeRaw,
                    [rehypeSanitize, markdownSanitizeSchema],
                  ]}
                >
                  {step.value}
                </ReactMarkdown>
              ) : (
                <p className="text-muted-foreground">No result yet.</p>
              )}
            </div>
          )}
          {saveError && isStepEditing && (
            <p className="text-destructive text-sm">{saveError}</p>
          )}
        </CardContent>
      )}

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Report Step</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the step "{stepTitle}"? This
              action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelDelete}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={isDeleting}
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
